// Admin memory-pressure analytics dashboard.
//
// Endpoints under /api/admin/memory/*  — see src/server/routes/admin-memory.js.
// HTML markup lives in <section data-screen="memory"> in apps/admin/index.html.
//
// Pattern mirrors apps/admin/src/screens/analytics.js: Chart.js is loaded from
// the locally bundled dependency, with the same accent colors and
// Promise.allSettled() over the section loaders so a single failed query
// doesn't take down the whole dashboard.

import { esc } from '../../../../src/shared/escape-html.js';

let chartPromise = null;
function loadChartJs() {
  if (window.Chart) return Promise.resolve(window.Chart);
  if (chartPromise) return chartPromise;
  chartPromise = import('chart.js/auto')
    .then(({ default: Chart }) => {
      window.Chart = Chart;
      return Chart;
    })
    .catch((error) => {
      chartPromise = null;
      throw error;
    });
  return chartPromise;
}

const _charts = new Map();
function destroyChart(key) {
  const c = _charts.get(key);
  if (c) { try { c.destroy(); } catch {} _charts.delete(key); }
}
function renderChart(key, canvas, config) {
  destroyChart(key);
  if (!window.Chart) throw new Error('Chart.js is not loaded');
  const chart = new window.Chart(canvas, config);
  _charts.set(key, chart);
  return chart;
}

// Same palette as the analytics screen so the two read consistently.
const ACCENT  = '#00e5a0';
const ACCENT2 = '#00b8ff';
const WARN    = '#ffb454';   // pressure
const DANGER  = '#ff4060';   // critical / tab-kill
const DIM     = '#888';

const DEFAULT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: '#ccc', font: { family: 'JetBrains Mono, monospace', size: 10 } } },
    tooltip: { titleFont: { family: 'JetBrains Mono' }, bodyFont: { family: 'JetBrains Mono' } },
  },
  scales: {
    x: { ticks: { color: DIM, font: { family: 'JetBrains Mono', size: 9 } }, grid: { color: '#2a2a2a' } },
    y: { ticks: { color: DIM, font: { family: 'JetBrains Mono', size: 9 } }, grid: { color: '#2a2a2a' }, beginAtZero: true },
  },
};

function fmtInt(n) { return (n || 0).toLocaleString(); }

async function fetchJson(url) {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json();
}

function relTime(iso) {
  if (!iso) return '–';
  const d = (typeof iso === 'string') ? new Date(iso) : iso;
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ── Loaders ──────────────────────────────────────────────

async function loadSummary() {
  try {
    const data = await fetchJson('/api/admin/memory/summary');
    setTile('mem-tile-sessions',   fmtInt(data.active_sessions_today));
    setTile('mem-tile-pressure',   fmtInt(data.pressure_events_today));
    setTile('mem-tile-mitigation', fmtInt(data.mitigation_total_today));
    setTile('mem-tile-tabkill',    fmtInt(data.tab_kill_crashes_today));
    // Ladder breakdown under the mitigation tile so admins can see
    // which level is firing most.
    const breakdown = data.mitigation_events_today || {};
    setTile('mem-tile-mitigation-sub',
      `L1 ${fmtInt(breakdown[1])} · L2 ${fmtInt(breakdown[2])} · L3 ${fmtInt(breakdown[3])}`);
  } catch (err) {
    console.warn('[memory] summary load failed:', err);
  }
}

function setTile(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

async function loadHeatmap(range = '7d') {
  const canvas = document.getElementById('mem-chart-heatmap');
  if (!canvas) return;
  try {
    await loadChartJs();
    const data = await fetchJson('/api/admin/memory/heatmap?range=' + encodeURIComponent(range));

    // Pivot into a grouped bar chart: x = system_id, series = device_class,
    // stacked counts of pressure + critical separately so admins can see
    // which combos are getting close to tab-killing.
    const systems = Array.from(new Set(data.cells.map(c => c.system_id))).sort();
    const devices = Array.from(new Set(data.cells.map(c => c.device_class))).sort();
    const byKey = new Map();
    for (const c of data.cells) byKey.set(`${c.system_id}|${c.device_class}|${c.to_state}`, c.events);

    const colorFor = (device, state) => {
      const base = state === 'critical' ? DANGER : WARN;
      // Tint by device class so the eye can see "mobile is the hotspot."
      const tints = {
        'mobile':       1.0,
        'tablet':       0.85,
        'desktop-low':  0.7,
        'desktop-mid':  0.55,
        'desktop-high': 0.4,
        'unknown':      0.3,
      };
      const opacity = tints[device] ?? 0.5;
      return base + Math.round(opacity * 255).toString(16).padStart(2, '0');
    };

    const datasets = [];
    for (const device of devices) {
      for (const state of ['pressure', 'critical']) {
        datasets.push({
          label: `${device} · ${state}`,
          data: systems.map(sys => byKey.get(`${sys}|${device}|${state}`) || 0),
          backgroundColor: colorFor(device, state),
          stack: device,
        });
      }
    }

    renderChart('heatmap', canvas, {
      type: 'bar',
      data: { labels: systems, datasets },
      options: {
        ...DEFAULT_OPTIONS,
        scales: {
          x: { ...DEFAULT_OPTIONS.scales.x, stacked: true },
          y: { ...DEFAULT_OPTIONS.scales.y, stacked: true },
        },
      },
    });
  } catch (err) {
    console.warn('[memory] heatmap load failed:', err);
  }
}

async function loadOffenders(range = '7d') {
  const tbody = document.getElementById('mem-offenders-tbody');
  if (!tbody) return;
  tbody.innerHTML = '<tr><td colspan="6" class="memory-loading">Loading&hellip;</td></tr>';
  try {
    const data = await fetchJson(
      `/api/admin/memory/top-offenders?range=${encodeURIComponent(range)}&limit=20`,
    );
    if (!data.offenders.length) {
      tbody.innerHTML = '<tr><td colspan="6" class="memory-empty">No pressure events in this window.</td></tr>';
      return;
    }
    tbody.innerHTML = data.offenders.map(o => `
      <tr data-system="${esc(o.system_id)}" data-rom="${esc(o.rom_hash)}">
        <td>${esc(o.title || '–')}</td>
        <td><span class="badge">${esc(o.system_id || '–')}</span></td>
        <td class="num">${fmtInt(o.events)}</td>
        <td class="num">${fmtInt(o.critical_events)}</td>
        <td class="num">${fmtInt(o.mitigations)}</td>
        <td class="num">${o.avg_js_heap_mb != null ? `${o.avg_js_heap_mb} MB` : '–'}</td>
      </tr>
    `).join('');

    // Wire row clicks to load the trajectory drill-down.
    tbody.querySelectorAll('tr[data-system]').forEach(tr => {
      tr.addEventListener('click', () => {
        const sys = tr.dataset.system;
        const rom = tr.dataset.rom;
        if (!sys || !rom) return;
        tbody.querySelectorAll('tr.active').forEach(r => r.classList.remove('active'));
        tr.classList.add('active');
        loadTrajectory(sys, rom);
      });
    });
  } catch (err) {
    console.warn('[memory] offenders load failed:', err);
    tbody.innerHTML = '<tr><td colspan="6" class="memory-empty">Failed to load.</td></tr>';
  }
}

async function loadTrajectory(systemId, romHash) {
  const canvas = document.getElementById('mem-chart-trajectory');
  const label = document.getElementById('mem-trajectory-label');
  if (!canvas) return;
  if (label) label.textContent = `${systemId} · ${romHash}`;
  try {
    await loadChartJs();
    const data = await fetchJson(
      `/api/admin/memory/trajectory?system_id=${encodeURIComponent(systemId)}` +
      `&rom_hash=${encodeURIComponent(romHash)}&limit=50`,
    );
    if (!data.events.length) {
      destroyChart('trajectory');
      return;
    }
    // Sort oldest → newest for a left-to-right time axis.
    const events = [...data.events].reverse();
    const labels = events.map(e => relTime(e.ts));

    renderChart('trajectory', canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          { label: 'JS heap (MB)',  data: events.map(e => e.js_heap_mb),   borderColor: ACCENT,  backgroundColor: ACCENT + '33',  tension: 0.15, fill: false, yAxisID: 'y' },
          { label: 'WASM heap (MB)', data: events.map(e => e.wasm_heap_mb), borderColor: ACCENT2, backgroundColor: ACCENT2 + '33', tension: 0.15, fill: false, yAxisID: 'y' },
          { label: 'Growth 5m (KB/s)', data: events.map(e => e.growth_5m_kbps), borderColor: WARN, backgroundColor: WARN + '33', tension: 0.15, fill: false, yAxisID: 'y1' },
        ],
      },
      options: {
        ...DEFAULT_OPTIONS,
        scales: {
          x: DEFAULT_OPTIONS.scales.x,
          y:  { ...DEFAULT_OPTIONS.scales.y, position: 'left',  title: { display: true, text: 'MB',     color: DIM } },
          y1: { ...DEFAULT_OPTIONS.scales.y, position: 'right', title: { display: true, text: 'KB/s',   color: DIM }, grid: { display: false } },
        },
      },
    });
  } catch (err) {
    console.warn('[memory] trajectory load failed:', err);
  }
}

// ── Entry point ──────────────────────────────────────────

let _bound = false;
let _currentRange = '7d';

export function render() {
  if (!_bound) {
    _bound = true;
    document.querySelectorAll('.memory-range-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.memory-range-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        _currentRange = btn.dataset.range || '7d';
        Promise.allSettled([loadHeatmap(_currentRange), loadOffenders(_currentRange)]);
      });
    });
  }

  Promise.allSettled([
    loadSummary(),
    loadHeatmap(_currentRange),
    loadOffenders(_currentRange),
  ]);
}
